import React from 'react';
import Chatbot from 'react-chatbot-kit'
import 'react-chatbot-kit/build/main.css'
import config, {getArchiveKey} from './bot/config.js';
import MessageParser from './bot/MessageParser.jsx';
import ActionProvider from './bot/ActionProvider.jsx';
import {useWorkspaceContext} from '../../context/workspaceContext';

import ClearAllIcon from '@mui/icons-material/ClearAll';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import {Box, Button, FormControl, InputLabel, MenuItem, Select, Tooltip, Typography} from '@mui/material';
import {ResourceType} from '../../@types/resource-type';
import {resourceIcon} from '../../utils/resource-icons';
import LinearProgress from '@mui/material/LinearProgress';
import {ChatBotActionType} from '../../@types/chatbot-action-type';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import LlmService from '../../services/llm.service';
import {toast} from 'react-toastify';

const saveMessages = (messages: any, eventSequenceId: number | null): void => {
    const key = eventSequenceId ? getArchiveKey(eventSequenceId) : config.archiveName;
    localStorage.setItem(key, JSON.stringify(messages));
};

const loadMessages = (eventSequenceId: number | null): any => {
    const key = eventSequenceId ? getArchiveKey(eventSequenceId) : config.archiveName;
    const messages = localStorage.getItem(key);
    return messages ? JSON.parse(messages) : [];
};

export default function ChatBotEditor(prop: { panelSizePercent: (number | string)[] }): React.ReactElement {
    const { panelSizePercent } = prop;
    const [showBot, toggleBot] = React.useState(false);
    const [includeESDChecked, setIncludeESDChecked] = React.useState(false);
    const [uploadingFile, setUploadingFile] = React.useState(false);
    const [llms, setLlms] = React.useState(['']);
    const [llm, setLlm] = React.useState('');
    const { currentWorkspaceObject, setChatBotAction } = useWorkspaceContext();

    // Event sequence detection for per-ES chat storage
    const isEventSequence = currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_TREES;
    const eventSequenceId = isEventSequence ? currentWorkspaceObject?.object?.id : null;

    // Key to force Chatbot remount when event sequence changes
    const [chatKey, setChatKey] = React.useState(0);

    React.useEffect(() => {
        toggleBot(panelSizePercent[2] !== 0)
    }, [panelSizePercent]);

    // Auto-check "Add ESD" when the active resource is an ESD
    React.useEffect(() => {
        if (isEventSequence) {
            setIncludeESDChecked(true);
            setChatBotAction({ actionType: ChatBotActionType.ADD_ESD, payload: true });
        } else {
            setIncludeESDChecked(false);
            setChatBotAction({ actionType: ChatBotActionType.ADD_ESD, payload: false });
        }
    }, [isEventSequence, eventSequenceId]);

    // Reload messages when event sequence changes
    React.useEffect(() => {
        if (showBot) {
            setChatKey(prev => prev + 1);
        }
    }, [eventSequenceId, showBot]);

    React.useEffect(() => {
        if (!showBot) {
            return;
        }
        LlmService.getLlmModels().then((response) => {
            setLlms(response.data.sort().reverse());
            setLlm(response.data[0]);
            setChatBotAction({ actionType: ChatBotActionType.LLM_MODEL, payload: response.data[0] });

        }).catch((error) => {
            toast.error('Error getting LLM - ' + error.message);
        });
    }, [showBot]);

    // Re-dispatch the selected model to ActionProvider after Chatbot remounts (chatKey changed).
    // ActionProvider loses its local llmModel state on remount, so we must re-send it.
    React.useEffect(() => {
        if (showBot && llm) {
            setChatBotAction({ actionType: ChatBotActionType.LLM_MODEL, payload: llm });
        }
    }, [chatKey]);

    //
    // NB: The react-chatbot-kit input element only allows for single line input
    // This useEffect is used to replace the input element with a textarea element
    // This is done to allow the user to input multiple lines of text   
    // The useEffect should be removed one day when the react-chatbot-kit input element 
    // is updated to allow for multiple lines of text
    //
    React.useEffect(() => {
        let input = document.getElementsByClassName('react-chatbot-kit-chat-input')[0];
        if (!input || (input instanceof HTMLTextAreaElement)) {
            return;
        }

        const textarea = document.createElement('textarea');  
        textarea.className = 'react-chatbot-kit-chat-input';
        textarea.rows = 1;
        textarea.placeholder = 'Type a message...';
        textarea.style.maxHeight = '200px'; // Set the maximum height of the textarea to 200px
        textarea.style.resize = 'none'; // Remove the resize handle from the textarea

        const adjustTextareaHeight = (textarea: HTMLTextAreaElement): void  => {
            
            textarea.style.height = 'auto';
            textarea.style.height = `${textarea.scrollHeight}px`;
        };

        textarea.oninput = (e: any): void => {
            textarea.value = e.target.value;
            if (textarea.value.trim() === '') {
                textarea.style.height = ''; // Reset height to default when empty
            } else {
                adjustTextareaHeight(textarea);
            }
        };
        
        input.replaceWith(textarea);

        const form = document.getElementsByClassName('react-chatbot-kit-chat-input-form')[0] as HTMLInputElement;
        form.onsubmit = (e: any): void => {
            e.preventDefault();
            if (textarea.value.trim() === '') {
                return;
            }
            setChatBotAction({ actionType: ChatBotActionType.TRIGGER_REPLY, payload: textarea.value });
            textarea.value = '';
            textarea.style.height = ''; // Reset height to default when empty
        }
    }, [chatKey, setChatBotAction]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const uploadFile = (): void => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (event: any): void => {
            const file = event.target.files[0];
            const reader = new FileReader();
            reader.onload = (e: any): void => {
                setChatBotAction({
                    actionType: ChatBotActionType.UPLOAD_FILE,
                    payload: {
                        name: file.name,
                        contents: e.target.result
                    }
                });
            };
            reader.onloadstart = (): void => {
                setUploadingFile(true);
            };
            reader.onloadend = (): void => {
                setUploadingFile(false);
            };
            reader.readAsText(file);
            
        }
        input.click();
    }

    
    // Added showBot to trigger the Chatbot saveMessages function
    // NB: The ChatBot triggers saveMessages function only when it is unmounted.
    return (
        <div style={{height: '100%', width: '100%', backgroundColor: '#FAFEFE'}}>
            {showBot && <>
                <Chatbot
                    key={chatKey}
                    config={config}
                    messageParser={MessageParser}
                    actionProvider={ActionProvider}
                    saveMessages={(messages: any): void => saveMessages(messages, eventSequenceId)}
                    messageHistory={loadMessages(eventSequenceId)}

                    validator={
                        (input: string): boolean => {
                            return input.trim() === '' ? false : true;
                        }
                    }
                />
        
             
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start',  marginLeft: '5px' }}>
                    {uploadingFile && <LinearProgress color="secondary" />}
                    <FormControl size='small'>
                        <InputLabel id='llm-label'>LLM</InputLabel>
                        <Select
                            labelId='llm-label'
                            id='llm'
                            value={llms && llms.length > 0 ? llm : ''}
                            label='LLM'
                            onChange={
                                (e): void => {
                                    setLlm(e.target.value);
                                    setChatBotAction({ actionType: ChatBotActionType.LLM_MODEL, payload: e.target.value });
                                }}
                        >
                            {llms && llms.map((model: string) => (
                                <MenuItem key={model} value={model}>{model}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isEventSequence && (
                        <Tooltip
                            title="Include the Safety View structure in the conversation context to help the AI understand and answer questions about your risk model"
                            arrow
                            placement="top"
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size='small'
                                        checked={includeESDChecked}
                                        onChange={
                                            (e): void => {
                                                setIncludeESDChecked(e.target.checked);
                                                setChatBotAction({ actionType: ChatBotActionType.ADD_ESD, payload: e.target.checked });
                                            }
                                        }
                                    />
                                }
                                label={<Typography variant='body2'>Add Safety View</Typography>}
                                labelPlacement='end'
                            />
                        </Tooltip>
                    )}
                    <Button
                        size='small'
                        variant='contained'
                        color='primary'
                        aria-label='clear all'
                        onClick={(): void => { setChatBotAction({ actionType: ChatBotActionType.CLEAR_HISTORY, payload: null}); }}
                        startIcon={<ClearAllIcon />}
                        sx={{ textTransform: 'none' }}
                    >
                        Clear
                    </Button>
                </div>
                {isEventSequence && currentWorkspaceObject?.object && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 1,
                            py: 1,
                            background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                            borderTop: '1px solid rgba(25, 118, 210, 0.2)',
                            color: '#1565c0'
                        }}
                    >
                        <Box sx={{ color: '#1976d2', display: 'flex', alignItems: 'center' }}>
                            {resourceIcon(ResourceType.EVENT_SEQUENCE_TREES)}
                        </Box>
                        <Box
                            key={eventSequenceId}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                animation: 'scrollIn 3s ease-out forwards',
                                '@keyframes scrollIn': {
                                    '0%': {
                                        transform: 'translateX(100%)',
                                        opacity: 0.5
                                    },
                                    '60%': {
                                        transform: 'translateX(0%)',
                                        opacity: 1
                                    },
                                    '100%': {
                                        transform: 'translateX(0%)',
                                        opacity: 1
                                    }
                                }
                            }}
                        >
                            <Typography
                                variant='caption'
                                sx={{
                                    fontWeight: 600,
                                    color: '#1565c0',
                                    letterSpacing: '0.02em'
                                }}
                            >
                                {currentWorkspaceObject.object.uniqueId}
                            </Typography>
                            <Typography variant='caption' sx={{ color: '#64b5f6' }}>
                                —
                            </Typography>
                            <Typography
                                variant='caption'
                                sx={{
                                    color: '#1976d2',
                                    fontWeight: 500
                                }}
                            >
                                {currentWorkspaceObject.object.name}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </>}
        </div>
    );
}